import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Space,
  Switch,
  TimePicker,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import CustomButton from 'components/custom-button';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import styles from '../styles.module.scss';
import { CHECK_FILE } from 'utils/rule-form';
import CustomizedUpload from 'components/customized-upload';
import { UPLOAD_TYPE } from 'constants/common';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import {
  DATE_TIME_FORMAT,
  describeDailyWindow,
  describeDateRange,
  fromVnWallClock,
  ImageAdItem,
  nowInVn,
  parseTimeOfDay,
  TIME_FORMAT,
  toVnWallClock,
} from '../ad-schedule';

interface FormValues {
  image: { fileKey: string; fileUrl?: string } | null;
  url?: string;
  startAt?: Dayjs | null;
  endAt?: Dayjs | null;
  useDailyWindow?: boolean;
  dailyWindow?: [Dayjs | null, Dayjs | null] | null;
}

interface Props {
  isOpen: boolean;
  /** Có = sửa ảnh này; không có = thêm mới. */
  record?: ImageAdItem | null;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
}

const SERVER_ERRORS: Record<string, string> = {
  AD_END_BEFORE_START: 'Thời gian kết thúc phải sau thời gian bắt đầu.',
  AD_DAILY_WINDOW_INCOMPLETE: 'Khung giờ mỗi ngày cần có cả giờ bắt đầu và giờ kết thúc.',
  AD_DAILY_WINDOW_EMPTY: 'Giờ bắt đầu và giờ kết thúc trong ngày không được trùng nhau.',
  IMAGE_ADS_NOT_FOUND: 'Ảnh quảng cáo không còn tồn tại (có thể đã bị xoá).',
};

const endOfDay = (d: Dayjs) => d.hour(23).minute(59).second(59).millisecond(0);

// Giờ mặc định khi chỉ chọn ngày: bắt đầu 00:00:00, kết thúc 23:59:59.
const DEFAULT_START_TIME = dayjs('00:00:00', TIME_FORMAT);
const DEFAULT_END_TIME = dayjs('23:59:59', TIME_FORMAT);

const ModalCreateImageAds = ({
  isOpen,
  record,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const editingId = record?.id;
  const isEdit = !!editingId;

  const startAt = Form.useWatch('startAt', form);
  const endAt = Form.useWatch('endAt', form);
  const useDailyWindow = Form.useWatch('useDailyWindow', form);
  const dailyWindow = Form.useWatch('dailyWindow', form);

  useEffect(() => {
    if (!record) return;
    const dailyStart = parseTimeOfDay(record.dailyStartTime);
    const dailyEnd = parseTimeOfDay(record.dailyEndTime);
    form.setFieldsValue({
      image: record.image
        ? { fileKey: record.image, fileUrl: `${SERVER_FILE_ENDPOINT}/${record.image}` }
        : null,
      url: record.url || '',
      startAt: toVnWallClock(record.startAt),
      endAt: toVnWallClock(record.endAt),
      useDailyWindow: !!(dailyStart && dailyEnd),
      dailyWindow: dailyStart && dailyEnd ? [dailyStart, dailyEnd] : undefined,
    });
  }, [form, record]);

  const onFinish = async (values: FormValues) => {
    const [dailyStart, dailyEnd] = (values.useDailyWindow && values.dailyWindow) || [];
    const payload = {
      image: values.image?.fileKey,
      url: values.url?.trim() || null,
      startAt: fromVnWallClock(values.startAt),
      endAt: fromVnWallClock(values.endAt),
      dailyStartTime: dailyStart ? dailyStart.format(TIME_FORMAT) : null,
      dailyEndTime: dailyEnd ? dailyEnd.format(TIME_FORMAT) : null,
    };

    setLoading(true);
    try {
      if (isEdit) {
        await AxiosUtils.patch(`${API.editAds}/${editingId}`, payload);
      } else {
        await AxiosUtils.post(API.postAds, payload);
      }
      handleCancelCreateEditModal();
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: isEdit ? 'Đã lưu thay đổi.' : 'Thêm thành công!',
        duration: 4,
      });
    } catch (error: any) {
      const raw = Array.isArray(error?.message) ? error.message[0] : error?.message;
      notification.error({
        message: 'Không thành công',
        description: SERVER_ERRORS[raw] || 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  // Chọn nhanh giờ tắt: luôn là 23:59:59 của ngày đó (sự kiện thường đóng cuối ngày).
  const quickEnds: Array<{ label: string; value: () => Dayjs }> = [
    { label: 'Hết hôm nay', value: () => endOfDay(nowInVn()) },
    { label: 'Hết 7 ngày tới', value: () => endOfDay(nowInVn().add(6, 'day')) },
    { label: 'Hết tháng này', value: () => endOfDay(nowInVn().endOf('month')) },
  ];

  const [dailyStart, dailyEnd] = (useDailyWindow && dailyWindow) || [];
  const summary = [
    describeDateRange(fromVnWallClock(startAt), fromVnWallClock(endAt)),
    describeDailyWindow(dailyStart?.format(TIME_FORMAT), dailyEnd?.format(TIME_FORMAT)),
  ].join(' · ');

  return (
    <Modal
      forceRender
      open={isOpen}
      title={isEdit ? `Sửa ảnh quảng cáo #${editingId}` : 'Thêm ảnh quảng cáo'}
      onCancel={handleCancelCreateEditModal}
      width={760}
      getContainer={false}
      footer={false}
    >
      <Form
        name="ads-form"
        form={form}
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        requiredMark={false}
        initialValues={{ useDailyWindow: false }}
        // Enter trong ô ngày/giờ chỉ để chốt giá trị vừa gõ, không được gửi form
        // (trước đây gõ giờ bắt đầu rồi Enter là lưu luôn, chưa kịp nhập giờ kết thúc).
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.target as HTMLElement).closest('.ant-picker')) {
            e.preventDefault();
          }
        }}
      >
        <Form.Item label="Ảnh" name="image" rules={[CHECK_FILE]}>
          <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={20} />
        </Form.Item>

        <Form.Item
          label="Liên kết khi bấm vào"
          name="url"
          rules={[{ max: 500, message: 'Không được quá 500 kí tự' }]}
        >
          <Input placeholder="Nhập liên kết (https://…) — bỏ trống nếu không cần" size="large" />
        </Form.Item>

        <div className={styles.sectionTitle}>Thời gian chạy</div>
        <div className={styles.sectionHint}>
          Giờ Việt Nam, chính xác tới từng giây. Bỏ trống = không giới hạn.
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Bắt đầu" name="startAt">
              <DatePicker
                showTime={{ defaultValue: DEFAULT_START_TIME }}
                format={DATE_TIME_FORMAT}
                placeholder="Chạy ngay"
                size="large"
                style={{ width: '100%' }}
                allowClear
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Kết thúc"
              name="endAt"
              dependencies={['startAt']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value?: Dayjs | null) {
                    const start: Dayjs | null = getFieldValue('startAt');
                    if (!value || !start || value.isAfter(start)) return Promise.resolve();
                    return Promise.reject(new Error('Kết thúc phải sau thời gian bắt đầu.'));
                  },
                }),
              ]}
              extra={
                <Space size={0} wrap className={styles.quickPicks}>
                  {quickEnds.map((q) => (
                    <Button
                      key={q.label}
                      type="link"
                      size="small"
                      onClick={() => {
                        form.setFieldValue('endAt', q.value());
                        form.validateFields(['endAt']);
                      }}
                    >
                      {q.label}
                    </Button>
                  ))}
                </Space>
              }
            >
              <DatePicker
                showTime={{ defaultValue: DEFAULT_END_TIME }}
                format={DATE_TIME_FORMAT}
                placeholder="Không hẹn ngày tắt"
                size="large"
                style={{ width: '100%' }}
                allowClear
                disabledDate={(d) => !!startAt && d.isBefore(startAt, 'day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <label className={styles.switchRow}>
          <Form.Item name="useDailyWindow" valuePropName="checked" noStyle>
            <Switch size="small" />
          </Form.Item>
          <span>Chỉ chạy trong khung giờ nhất định mỗi ngày</span>
        </label>

        {useDailyWindow && (
          <Form.Item
            label="Khung giờ mỗi ngày"
            name="dailyWindow"
            extra="Giờ bắt đầu lớn hơn giờ kết thúc = khung qua đêm, vd 20:00:00 → 02:00:00."
            rules={[
              {
                validator(_, value?: [Dayjs | null, Dayjs | null] | null) {
                  if (!value?.[0] || !value?.[1]) {
                    return Promise.reject(new Error('Chọn đủ giờ bắt đầu và giờ kết thúc.'));
                  }
                  if (value[0].format(TIME_FORMAT) === value[1].format(TIME_FORMAT)) {
                    return Promise.reject(new Error('Hai mốc giờ không được trùng nhau.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TimePicker.RangePicker
              format={TIME_FORMAT}
              order={false}
              size="large"
              placeholder={['Từ giờ', 'Đến giờ']}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Alert className={styles.summary} type="info" showIcon message={summary} />

        <div className="d-flex justify-center">
          <Space size="large">
            <CustomButton
              bgColor="secondary-green"
              text={isEdit ? 'Lưu thay đổi' : 'Lưu'}
              htmlType="submit"
              loading={loading}
            />
            <CustomButton
              text="Hủy"
              bgColor="primary-grey"
              htmlType="button"
              onClick={handleCancelCreateEditModal}
            />
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default ModalCreateImageAds;
